import sharedb from 'sharedb/lib/client'
import type { ShallowRef } from 'vue'
import { onUnmounted, ref, shallowRef, watch } from 'vue'
import { fabric } from 'fabric'
import ReconnectingWebSocket from 'reconnecting-websocket'
import _debounce from 'lodash-es/debounce'
import type { ICanvasPanEvent, ICanvasZoomEvent } from '../services/whiteboard'
import type Whiteboard from '../services/whiteboard'
import type WbDynamic from '../services/dynamic'
import type WbArticleImage from '../services/articleImage'
import type WbDiscussionIcon from '../services/discussionIcon'
import type { IPresenceRecord } from './presence'
import appConfig from '@/services/appConfig'
import { useUserStore } from '@/store/userData'
import utils from '@/services/utils'
import { whiteboardConstants } from '@/models/constants'
import { getArticleAssets } from '@/api/t1/article'

interface IUserData {
  zoomFactor: number
  zoomPoint?: IPoint
  panViewport: number[]
}

export default function useCollaboration(whiteboardId: number, wb: ShallowRef<Whiteboard | undefined>) {
  const boardId = `${appConfig.T1Env}${whiteboardId}`
  const dbConn = shallowRef<sharedb.Connection>()
  const dbDoc = shallowRef<sharedb.Doc>()
  const userDoc = shallowRef<sharedb.Doc>()
  const userStore = useUserStore()
  const collaborationReady = ref(false)
  const connected = ref(false)
  let userData: IUserData = { zoomFactor: 1, panViewport: [] }
  let presence: sharedb.Presence
  let localPresence: sharedb.LocalPresence<IPresenceRecord>
  let ws: ReconnectingWebSocket

  watch(wb, () => {
    if (dbConn.value) {
      dbConn.value.close()
      dbConn.value = undefined
    }

    if (dbDoc.value) {
      dbDoc.value.destroy(() => register())
      dbDoc.value = undefined
    }
    else {
      register()
    }
  }, { immediate: true })

  onUnmounted(() => {
    if (dbDoc.value) { dbDoc.value.destroy() }
    if (userDoc.value) { userDoc.value.destroy() }
    if (localPresence) { localPresence.destroy() }
    if (presence) { presence.destroy() }
    if (ws) { ws.close(4999, 'Unmounted') }
  })

  function register() {
    if (!wb.value) { return }
    collaborationReady.value = false

    // Create WebSocket
    console.log('Registering', appConfig)
    ws = new ReconnectingWebSocket(`${appConfig.ShareDbUrl}/server?t=${encodeURIComponent(localStorage.getItem('tk') || '')}`)
    ws.addEventListener('open', () => {
      connected.value = true
      console.log('Connected to WS')
    })
    ws.addEventListener('close', (e) => {
      connected.value = false
      console.log('Disconnected from WS', e)
    })

    // Create connection
    // eslint-disable-next-line ts/ban-ts-comment
    // @ts-expect-error
    dbConn.value = new sharedb.Connection(ws)
    dbDoc.value = dbConn.value.get('boards', boardId)
    // Subscribe to changes in the document
    dbDoc.value.subscribe((err) => {
      if (err) {
        console.error(err)
        return
      }

      // if document has been fetch but type is not set it means document has not been created yet https://share.github.io/sharedb/api/doc#type--type
      if (!dbDoc.value?.type) {
        // Document does not exist, create it
        dbDoc.value?.create({ version: 0 })
        collaborationReady.value = true
      }
      else {
        // Load initial objects
        const objectOrder = new Set<string>()
        if (dbDoc.value.data?.objectOrder && dbDoc.value.data.objectOrder.length) {
          dbDoc.value.data.objectOrder.forEach(id => objectOrder.add(id))
        }
        else {
          for (const key in dbDoc.value.data) {
            if (dbDoc.value.data[key].type === whiteboardConstants.objectTypes.frame) {
              objectOrder.add(key)
            }
          }
        }

        for (const key in dbDoc.value.data) {
          if (key !== 'version' && key !== 'objectOrder') {
            objectOrder.add(key)
          }
        }

        const objs: any[] = []
        const groups: any[] = []
        for (const key of objectOrder) {
          if (dbDoc.value.data[key]) {
            if (dbDoc.value.data[key].type !== whiteboardConstants.objectTypes.group) {
              objs.push(dbDoc.value.data[key])
            }
            else {
              groups.push(dbDoc.value.data[key])
            }
          }
        }

        const objectPromises = wb.value?.addObjectsFromJson(objs).then(() => {
          wb.value?.performMoveToObject()
        })

        const groupPromises = groups.map((group) => {
          if (group.objects && group.objects.length > 0) {
            return wb.value?.addGroupFromJson(group).then(() => {
              wb.value?.performMoveToObject()
            })
          }
          return Promise.resolve()
        })

        Promise.all([objectPromises, ...groupPromises])
          .then(() => {
            collaborationReady.value = true
            wb.value?.onCanavasObjectsLoaded(wb.value)
          })
          .catch((error) => {
            console.error('Error loading canvas objects:', error)
          })
      }
    })

    userDoc.value = dbConn.value.get('usersData', userStore.userProfile.UserName)
    userDoc.value.subscribe((err) => {
      if (err) {
        console.error(err)
        return
      }
      if (!userDoc.value?.type) {
        // Document does not exist, create it
        userDoc.value?.create({ version: 0 })
      }
      else if (utils.isDefined(userDoc.value?.data) && utils.isDefined(userDoc.value?.data[boardId])) {
        userData = userDoc.value.data[boardId]
        if (userData.panViewport.length) {
          wb.value?.canvas.setViewportTransform(userData.panViewport)
        }
        wb.value?.zoom(userData.zoomFactor, userData.zoomPoint)
      }
    })

    // Handle operations from shareDB
    dbDoc.value.on('op', async (op, source) => {
      console.log('new op', op, source)
      if (source || !op || !op[0] || !op[0].p || !op[0].p[0]) { return }

      const objs: any = wb.value?.canvas.getObjects()

      if (op[0].p[0] === 'objectOrder') {
        const newOrder = op[0].oi
        if (newOrder && newOrder.length) {
          newOrder.forEach((id: string) => {
            const obj = objs.find(obj => obj.id === id)
            if (obj) {
              obj.bringToFront()
            }
          })

          wb.value!.reorderObjects()
          wb.value!.canvas.requestRenderAll()
        }
      }
      else {
        const obj = objs?.find(o => o.id === op[0].p[0])

        if (op[0].hasOwnProperty('oi')) {
          // Operation is to insert/update object
          if (obj) {
            // Set Current Lock
            const currentLock = obj.lock

            // Object exists, update
            const configuredImagekeys = (userStore.activeCatalog?.Config.WhiteboardImagePlaceholderOptions?.keys || []).map(key => key.toLowerCase())
            if (obj.type === whiteboardConstants.objectTypes.articleImage && op[0].oi.assetKey && configuredImagekeys.includes(op[0].oi.assetKey.toLowerCase())) {
              const catalogDetails = userStore.linkedCatalogDetails[obj.CatalogCode] || userStore.activeCatalog
              const assets = await getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, obj.articleNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)
              obj.assets = assets
              obj.imgLoaded = false
              obj.imgLoading = false
            }

            let applyFilter = false
            let applyTemplateOptions = false

            if (obj.type === whiteboardConstants.objectTypes.dynamic) {
              const dyn = obj as WbDynamic
              const currentFilter = JSON.stringify(dyn.filterJson)
              const currentTemplateOptions = JSON.stringify(dyn.templateOptions)

              if (currentTemplateOptions !== JSON.stringify(op[0].oi.templateOptions)) {
                applyTemplateOptions = true
                applyFilter = true
              }
              if (currentFilter !== JSON.stringify(op[0].oi.filterJson) || dyn.hideInactive !== op[0].oi.hideInactive || dyn.showLabels !== op[0].oi.showLabels) {
                applyFilter = true
              }
              dyn.set(op[0].oi)
              if (applyTemplateOptions) {
                dyn.reloadTemplate()
              }
              if (applyFilter) {
                dyn.applyFilter()
              }
            }

            const selObjs = wb.value!.canvas.getActiveObjects()
            if (selObjs && selObjs.length > 0 && selObjs.some((o: any) => o.id === obj.id)) {
              wb.value!.canvas.discardActiveObject()
              obj.set(op[0].oi)
              const sel = new fabric.ActiveSelection(selObjs, { canvas: wb.value!.canvas })
              wb.value!.canvas.setActiveObject(sel)
            }
            else {
              obj.set(op[0].oi)
            }

            if (currentLock !== obj.lock) {
              obj.setLock(obj.lock)
            }

            obj.dirty = true
            wb.value!.canvas.requestRenderAll()

            if (obj.type === whiteboardConstants.objectTypes.articleImage) {
              (obj as WbArticleImage).onServerUpdate()
            }

            if (obj.type === whiteboardConstants.objectTypes.discussion) {
              (obj as WbDiscussionIcon).calcUniqueUsers()
            }
            wb.value!.emitObjectModifiedServer(obj)
          }
          else {
            // New object, create
            const newObject = op[0].oi
            console.warn('adding', newObject)

            let articleExistInCurrentCatalog = true
            if (newObject.type && (newObject.type === whiteboardConstants.objectTypes.articleImage || newObject.type === whiteboardConstants.objectTypes.articleDetails)) {
              articleExistInCurrentCatalog = await appConfig.DB!.doesArticleIdExistInCatalog(newObject.articleId, userStore.activeCatalog!.CatalogCode)
            }
            else if (newObject.type && (newObject.type === whiteboardConstants.objectTypes.modelImage || newObject.type === whiteboardConstants.objectTypes.modelDetails)) {
              articleExistInCurrentCatalog = await appConfig.DB!.doesModelNumberExistInCatalog(newObject.modelNumber, userStore.activeCatalog!.CatalogCode)
            }

            // if added article does not exist in current catalog reload data to get added articles
            if (!articleExistInCurrentCatalog) {
              await userStore.doLoadData(['Articles'])
            }

            newObject.excludeFromExport = true
            wb.value?.addObjectsFromJson([newObject])
              .then(() => wb.value?.canvas.requestRenderAll())
              .finally(() => newObject.excludeFromExport = undefined)
          }
        }
        else if (op[0].hasOwnProperty('od')) {
          // Operation is to remove object
          if (obj) {
            console.warn('removing')
            wb.value?.canvas.remove(obj)
            wb.value?.canvas.requestRenderAll()
          }
        }
      }
    })

    // Handle events on the canvas
    // Object added
    wb.value.canvas.on('object:added', (e) => {
      try {
        const obj = e.target?.toObject()
        if (obj?.id && !e.target?.excludeFromExport && collaborationReady.value && !obj?.localOnly) {
          submitUpsertOp(obj)
        }
      }
      catch (error) {
        console.error('Error in object:added event:', e.target, error)
      }
    })

    // Object modified
    wb.value.canvas.on('object:modified', (e) => {
      if (e.target) {
        if (e.target.isType('activeSelection')) {
          // If it's a selection, then iterate thru all objects within
          const objs = wb.value!.canvas.getActiveObjects()
          wb.value!.canvas.discardActiveObject()
          objs.forEach((o: any) => {
            const obj = o.toObject()
            if (obj?.id && collaborationReady.value && !obj?.localOnly) {
              submitUpsertOp(obj)
            }
          })
          const sel = new fabric.ActiveSelection(objs, { canvas: wb.value!.canvas })
          sel.setObjectsCoords()
          sel.setCoords()
          wb.value!.canvas.setActiveObject(sel)
          wb.value!.canvas.requestRenderAll()
        }
        else {
          const activeObjects = wb.value!.canvas.getActiveObjects()
          if (!e.subTargets && activeObjects.length > 1) {
            wb.value!.canvas.discardActiveObject()
            const obj: any = e.target
            if (obj?.id && collaborationReady.value && !obj?.localOnly) {
              submitUpsertOp(obj)
            }

            const sel = new fabric.ActiveSelection(activeObjects, { canvas: wb.value!.canvas })
            sel.setObjectsCoords()
            sel.setCoords()
            wb.value!.canvas.setActiveObject(sel)
            wb.value!.canvas.requestRenderAll()
          }
          else {
            const obj = e.target.toObject()
            if (obj?.id && collaborationReady.value && !obj?.localOnly) {
              submitUpsertOp(obj)
            }
          }
        }
      }
    })

    // Object deleted
    wb.value.canvas.on('object:removed', (e) => {
      if (e.target) {
        const obj = e.target.toObject()
        if (obj.id && collaborationReady.value && !obj?.localOnly) {
          console.log('object:removed', obj)
          submitDeleteOp(obj)
        }
      }
    })

    // Objects Order
    wb.value.on('object-order', () => {
      if (dbDoc.value && dbDoc.value.data && wb.value) {
        const objectOrder = wb.value.canvas.getObjects().filter(obj => !(obj as any).localOnly && obj.type !== whiteboardConstants.objectTypes.discussion && (obj as any).id?.length > 0).map((obj: any) => obj.id)
        submitUpsertOp(objectOrder, 'objectOrder')
      }
    })

    wb.value.on('zoom', (e: ICanvasZoomEvent) => {
      userData.zoomFactor = e.factor
      userData.zoomPoint = e.point
      userDoc.value?.submitOp([{ p: [boardId], oi: userData }])
    })

    const panDebounced = _debounce((e: ICanvasPanEvent) => {
      userData.panViewport = e.viewport
      userDoc.value?.submitOp([{ p: [boardId], oi: userData }])
    }, 500)
    wb.value.on('pan', panDebounced)
  }

  function submitUpsertOp(obj: any, id?: string) {
    const objectId = id || obj.id
    if (dbDoc.value && dbDoc.value.data && obj && objectId && collaborationReady.value) {
      if (dbDoc.value.data[objectId]) {
        if (JSON.stringify(obj) === JSON.stringify(dbDoc.value?.data[objectId])) {
          console.log('object already exists with same data', objectId, dbDoc.value?.data[objectId])
          return
        }
        console.log('object already exists, updating', objectId, dbDoc.value.data[objectId])
        dbDoc.value.submitOp([{ p: [objectId], od: dbDoc.value.data[objectId], oi: obj }], {}, shareDBCallback)
      }
      else {
        console.log('object does not exist, adding', obj)
        dbDoc.value.submitOp([{ p: [objectId], oi: obj }], {}, shareDBCallback)
      }
    }
  }

  function submitDeleteOp(obj: any, id?: string) {
    const objectId = id || obj.id
    if (dbDoc.value && dbDoc.value.data && obj && objectId && collaborationReady.value) {
      if (dbDoc.value.data[objectId]) {
        console.log('object exists, deleting', objectId)
        dbDoc.value.submitOp([{ p: [objectId], od: obj }], {}, shareDBCallback)
      }
      else {
        console.warn('object does not exist, cannot delete', obj.id)
      }
    }
  }

  function shareDBCallback(error: any) {
    if (error) {
      console.error('ShareDB Error:', error)
    }
  }

  return { dbConn, dbDoc, userDoc, collaborationReady, connected }
}
